<template>
  <div class="options__search">
    <h2 class="options__filters-heading">
      Filters
    </h2>
    <div
      :class="[
        'options__search-bar',
        { 'options__search-bar--filled': searchText.length > 0 }
      ]"
    >
      <div
        v-if="searchText.length > 0"
        class="options__search-bar-clear"
        @click="clearSearch"
      />
      <input
        v-model="searchText"
        placeholder="Search by name"
        @input="updateOptionSearchText"
      >
    </div>
    <div class="options__filters-body">
      <ElCollapse>
        <ElCollapseItem
          v-for="(groupName, index) in groupNames"
          :key="index"
          :title="groupName"
          class="options__filters-filter"
        >
          <Check
            v-for="(tag, index) in groupItems(groupName)"
            :key="index"
            :label="tag.value"
            :the-data="tag"
            :checked="isFiltered(tag)"
            class="checkbox"
            @checked-changed="filterOptions($event)"
          />
        </ElCollapseItem>
      </ElCollapse>
    </div>
    <div class="options__filters-footer">
      <ElButton
        type="text"
        :disabled="hasNoSelection"
        @click="resetFilters"
      >
        Reset
      </ElButton>
    </div>
  </div>
</template>

<script>
import debounce from 'debounce'
import _isEqual from 'lodash/isEqual'
// fetchFilters, getCompiledFilters, parseAppliedFilters
import { Filters } from '@/mixins/filters'

import Check from '@/components/Check.vue'

export default {
  name: 'StyleOptionFilters',

  components: {
    Check
  },

  mixins: [Filters],

  props: {
    appliedFilters: {
      type: Array,
      default: () => []
    },
    search: {
      type: String,
      default: ''
    },
    displayOptions: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    searchText: '',
    localAppliedFilters: [],
    filterGroups: []
  }),

  computed: {
    /**
     * @returns {Array}
     */
    groupNames () {
      return Object.keys(this.filterGroups)
    },

    /**
     * @returns {boolean}
     */
    hasNoSelection () {
      return this.localAppliedFilters.length === 0 && this.searchText === ''
    }
  },

  watch: {
    /**
     * @param {object} newValue
     * @param {object} oldValue
     */
    search (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateOptionFilters()
      }
    },

    /**
     * @param {object} newValue
     */
    appliedFilters (newValue) {
      this.compileFilters()

      if (
        JSON.stringify(newValue) !== JSON.stringify(this.localAppliedFilters)
      ) {
        this.updateOptionFilters()
      }
    },

    /**
     */
    displayOptions () {
      this.compileFilters()
      this.updateOptionFilters()
    }
  },

  async created () {
    await this.fetchFilters()

    this.compileFilters()
    this.searchText = this.search

    this.updateOptionFilters()
  },

  methods: {
    /**
     */
    compileFilters () {
      this.filterGroups = this.getCompiledFilters(this.displayOptions)

      this.localAppliedFilters = this.parseAppliedFilters(
        this.appliedFilters,
        this.displayOptions
      )
    },

    /**
     * @param   {object}  tag
     *
     * @returns {boolean}
     */
    isFiltered (tag) {
      return !!this.localAppliedFilters.find(filter =>
        _isEqual(filter, tag.filter)
      )
    },

    /**
     * @param   {string} name
     *
     * @returns {object}
     */
    groupItems (name) {
      return this.filterGroups[name]
    },

    /**
     * @param {object} checkPayload
     */
    filterItems (checkPayload) {
      const filter = checkPayload.theData.filter

      if (checkPayload.checked) {
        this.localAppliedFilters.push(filter)
      } else {
        const index = this.localAppliedFilters.findIndex(afilter =>
          _isEqual(afilter, filter)
        )
        this.localAppliedFilters.splice(index, 1)
      }
    },

    /**
     */
    updateOptionSearchText: debounce(
      /**
       *
       */
      function () {
        this.updateOptionFilters()
      }, 250),

    /**
     */
    updateOptionFilters () {
      this.$emit('filters-changed', {
        searchText: this.searchText,
        filters: [...this.localAppliedFilters]
      })
    },

    /**
     */
    resetFilters () {
      this.localAppliedFilters = []
      this.clearSearch()
    },

    /**
     * @param {object} payload
     */
    filterOptions (payload) {
      this.filterItems(payload)

      this.updateOptionFilters()
    },

    /**
     */
    clearSearch () {
      this.searchText = ''

      this.updateOptionFilters()
    }
  }
}
</script>

<style lang="scss" scoped>
$el-collapse-item-header-height: 22px;
$el-collapse-item-header-font-size: 16px;
$options-filters-group-bottom-margin: 10px;

.options__search {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.options__search-bar {
  @include size(100%, $spacer-unit-x4);

  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: $spacer-unit-x2;
  background-color: $grey-blue-light;
  border-radius: $border-radius-default;

  .options__search-bar-clear {
    position: absolute;
    top: 0;
    left: 0;
    width: $spacer-unit-x4;
    height: $spacer-unit-x4;
    border-radius: $border-radius-default;
    cursor: pointer;
  }

  input {
    @include size(inherit);

    position: relative;
    padding-left: spacing(0.5);
    text-align: left;
    background-color: transparent;
    border-radius: inherit;
  }

  &::before {
    @include size($gutter);

    margin-left: $spacer-unit;
    background-image: url('../../assets/svg/search.svg');
    background-repeat: no-repeat;
    background-position: center;
    background-size: 100%;
    transform: rotate(45deg);
    content: '';
  }
}

.options__search-bar--filled {
  &::before {
    background-image: url('../../assets/icons/icon-cross.svg');
    transform: rotate(0deg);
    opacity: 0.4;
  }
}

.options__filters {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: $max-width-aside-large;
}

.options__filters-title {
  color: $grey-light;
}

.options__filters-filter {
  margin-bottom: $options-filters-group-bottom-margin;

  &:last-of-type {
    margin-bottom: 0;
  }

  /deep/ .el-collapse-item__wrap {
    border-bottom: 0;
  }

  /deep/ .el-collapse-item__header {
    height: $el-collapse-item-header-height;
    color: $grey-light;
    font-size: $el-collapse-item-header-font-size;
    line-height: $el-collapse-item-header-height;
    border-bottom: 0;
  }

  /deep/ .el-collapse-item__content {
    padding-bottom: 0;
  }
}

.options__filters-heading {
  padding-bottom: $spacer-unit-x2;
  text-align: left;
}

.options__filters-body {
  @include scroll-y-smooth();

  margin-bottom: $spacer-unit-x2;

  /deep/ .el-collapse {
    border-top: 0;
    border-bottom: 0;
  }
}

.options__filters-footer {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: spacing(5);
}
</style>
